// Lazy-loading multi-pixel analytics. GA4 is already loaded unconditionally in
// public/index.html — this module adds Meta Pixel + LinkedIn Insight Tag
// (consent-gated), persists UTM params for the session, and broadcasts custom
// events to whichever pixels are currently loaded.
//
// Env vars (CRA reads from .env at build time):
//   REACT_APP_META_PIXEL_ID         — empty string disables Meta Pixel
//   REACT_APP_LINKEDIN_PARTNER_ID   — empty string disables LinkedIn tag

const CONSENT_KEY = 'bbg_consent_v1';
const UTM_KEY = 'bbg_utm_v1';
const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const META_STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Lead',
  'CompleteRegistration',
  'Contact',
  'Subscribe',
  'AddToCart',
  'Purchase',
]);

let pixelsInitialized = false;

const isBrowser = () => typeof window !== 'undefined';

export function getConsent() {
  if (!isBrowser()) return null;
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

export function hasConsent() {
  return getConsent() === 'granted';
}

export function setConsent(value) {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage unavailable (private mode + Safari) — silently ignore
  }
  if (value === 'granted') initAnalytics();
}

// UTM capture — runs regardless of consent because it's session-local state
// used to forward attribution onward to app.botibonga.com.
export function captureUTMs() {
  if (!isBrowser()) return {};

  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}');
  } catch {
    stored = {};
  }

  const params = new URLSearchParams(window.location.search);
  let updated = false;
  for (const field of UTM_FIELDS) {
    const value = params.get(field);
    if (value && stored[field] !== value) {
      stored[field] = value;
      updated = true;
    }
  }

  if (updated) {
    try {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(stored));
    } catch {
      // ignore
    }
  }

  return stored;
}

export function getUTMs() {
  if (!isBrowser()) return {};
  try {
    return JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}');
  } catch {
    return {};
  }
}

export function appendUTMsToUrl(url) {
  if (!isBrowser() || !url) return url;
  const utms = getUTMs();
  const keys = Object.keys(utms);
  if (!keys.length) return url;
  try {
    const target = new URL(url, window.location.origin);
    for (const key of keys) {
      if (!target.searchParams.has(key)) {
        target.searchParams.set(key, utms[key]);
      }
    }
    return target.toString();
  } catch {
    return url;
  }
}

function injectMetaPixel(pixelId) {
  if (window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );
  /* eslint-enable */
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

function injectLinkedInTag(partnerId) {
  if (window.lintrk) return;
  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);
  /* eslint-disable */
  (function (l) {
    if (!l) {
      window.lintrk = function (a, b) {
        window.lintrk.q.push([a, b]);
      };
      window.lintrk.q = [];
    }
    var s = document.getElementsByTagName('script')[0];
    var b = document.createElement('script');
    b.type = 'text/javascript';
    b.async = true;
    b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    s.parentNode.insertBefore(b, s);
  })(window.lintrk);
  /* eslint-enable */
}

export function initAnalytics() {
  if (!isBrowser() || pixelsInitialized) return;
  if (!hasConsent()) return;

  captureUTMs();

  const metaId = process.env.REACT_APP_META_PIXEL_ID;
  if (metaId) injectMetaPixel(metaId);

  const linkedInId = process.env.REACT_APP_LINKEDIN_PARTNER_ID;
  if (linkedInId) injectLinkedInTag(linkedInId);

  pixelsInitialized = true;
}

export function trackEvent(name, params = {}) {
  if (!isBrowser() || !hasConsent()) return;

  const enriched = { ...getUTMs(), ...params };

  if (typeof window.gtag === 'function') {
    window.gtag('event', name, enriched);
  }

  if (typeof window.fbq === 'function') {
    if (META_STANDARD_EVENTS.has(name)) {
      window.fbq('track', name, enriched);
    } else {
      window.fbq('trackCustom', name, enriched);
    }
  }

  // LinkedIn fires a generic conversion ping; conversion IDs are configured
  // per-event in the LinkedIn Campaign Manager UI, not in code.
  if (typeof window.lintrk === 'function') {
    window.lintrk('track', {});
  }
}

export function trackLead(payload = {}) {
  trackEvent('Lead', payload);
  if (payload.source) {
    trackEvent(`Lead_${payload.source}`, payload);
  }
}

// Web Vitals — async-imported to avoid blocking initial render.
// Sent to GA4 (and any other loaded pixels) once consent is granted.
export function reportWebVitals() {
  if (!isBrowser()) return;
  import('web-vitals').then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
    const send = (metric) => {
      // Round to integers (LCP/FCP/TTFB in ms) or 4-decimal (CLS).
      const value = metric.name === 'CLS'
        ? Math.round(metric.value * 1000) / 1000
        : Math.round(metric.value);

      // Web Vitals can fire before consent — buffer is too noisy to retain,
      // so we just drop pre-consent metrics rather than queue them.
      if (typeof window.gtag === 'function' && hasConsent()) {
        window.gtag('event', metric.name, {
          value,
          metric_id: metric.id,
          metric_rating: metric.rating,
          metric_delta: metric.delta,
        });
      }
    };

    onCLS(send);
    onINP(send);
    onLCP(send);
    onFCP(send);
    onTTFB(send);
  });
}
